import { Link } from "react-router";

import {
  ChevronRightIcon,
  DetailCardSection,
  DetailList,
  DetailRow,
  formatDateTime,
  IdValue,
} from "@prequel/shared-components";

import RowsFunnel, { type FunnelCard, getFunnelPercent } from "@/components/import/RowsFunnel";
import ImportJobStatusBadge from "@/components/ImportJobStatusBadge";
import { useExtractContext } from "@/pages/import/DatasetsPage/ExtractPage";
import { getJobStatus } from "@/pages/import/jobStatus";
import { getDuration } from "@/types";

const ExtractDetails = () => {
  const { extract, dataset } = useExtractContext();
  const processed = extract.records_processed;
  const recordsPath = `/import/datasets/${extract.dataset_id}/extracts/${extract.id}/records`;

  const cards: FunnelCard[] = [
    { label: "Processed Rows", value: processed },
    {
      label: "New",
      value: extract.records_new_inserted,
      percent: getFunnelPercent(extract.records_new_inserted, processed),
    },
    {
      label: "Updated",
      value: extract.records_existing_updated,
      percent: getFunnelPercent(extract.records_existing_updated, processed),
    },
    {
      label: "Unchanged",
      value: extract.records_existing_unchanged,
      percent: getFunnelPercent(extract.records_existing_unchanged, processed),
    },
    {
      label: "Deleted",
      value: extract.records_existing_deleted,
      percent: getFunnelPercent(extract.records_existing_deleted, processed),
    },
    {
      label: "Errored",
      value: extract.records_errored,
      percent: getFunnelPercent(extract.records_errored, processed),
      isError: true,
    },
  ];

  return (
    <div className="mt-6">
      <section className="mb-6 grid grid-cols-3 gap-4 rounded-md border border-stone-200 bg-white px-4 py-3">
        <div>
          <div className="text-xs tracking-wide text-stone-500 uppercase">Status</div>
          <div className="mt-1">
            <ImportJobStatusBadge status={getJobStatus(extract)} />
          </div>
        </div>
        <div>
          <div className="text-xs tracking-wide text-stone-500 uppercase">Total duration</div>
          <div className="mt-1 text-sm text-stone-800">{getDuration(extract)}</div>
        </div>
        <div>
          <div className="text-xs tracking-wide text-stone-500 uppercase">Error message</div>
          <div className="mt-1 text-sm text-stone-800">{extract.error?.message ?? "—"}</div>
        </div>
      </section>

      <h3 className="mb-2 text-sm font-medium text-stone-800">Summary</h3>
      <RowsFunnel cards={cards} recordsPath={recordsPath} />

      <DetailCardSection title="Details">
        <DetailList>
          <DetailRow label="ID" value={<IdValue id={extract.id} />} />
          <DetailRow
            label="Dataset"
            value={
              <Link
                to={`/import/datasets/${extract.dataset_id}`}
                className="
                  flex items-center gap-1 text-stone-800
                  hover:underline
                "
              >
                {dataset ? dataset.name : extract.dataset_id}
                <ChevronRightIcon className="text-stone-400" />
              </Link>
            }
          />
          <DetailRow label="Submitted" value={formatDateTime(extract.submitted_at)} />
          <DetailRow label="Started" value={extract.started_at ? formatDateTime(extract.started_at) : "—"} />
          <DetailRow label="Ended" value={extract.ended_at ? formatDateTime(extract.ended_at) : "—"} />
        </DetailList>
      </DetailCardSection>
    </div>
  );
};

export default ExtractDetails;
